import React, {
  useReducer,
  createContext,
  useContext,
  useCallback,
} from "react";
import { Alert } from "react-bootstrap";

export const initialState = {
  variant: null,
  message: null,
  visible: false,
};

// Reducer function for handling notification actions
export const notificationReducer = (state, action) => {
  switch (action.type) {
    case "SHOW_NOTIFICATION":
      return {
        ...state,
        variant: action.payload.variant,
        message: action.payload.message,
        visible: true,
      };
    case "CLEAR_NOTIFICATION":
      return initialState;
    default:
      return state;
  }
};

// Create a Notification context
const NotificationContext = createContext();

// Notification Provider Component
export const NotificationProvider = ({ children, defaultTimeout = 5000 }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  // Memoize the showNotification function using useCallback
  const showNotification = useCallback(
    (variant, message, timeout) => {
      dispatch({
        type: "SHOW_NOTIFICATION",
        payload: { variant, message },
      });

      // Use the defaultTimeout if no specific timeout is provided
      const duration = timeout || defaultTimeout;

      // Auto-dismiss notification after the timeout
      setTimeout(() => {
        dispatch({ type: "CLEAR_NOTIFICATION" });
      }, duration);
    },
    [defaultTimeout] // Dependency array for useCallback
  );

  return (
    <NotificationContext.Provider value={{ state, showNotification }}>
      {children}
      {state.visible && (
        <Alert
          variant={state.variant}
          onClose={() => dispatch({ type: "CLEAR_NOTIFICATION" })}
          dismissible
          className="position-fixed top-0 start-0 end-0 rounded-0"
          style={{ zIndex: 1050 }}
        >
          {state.message}
        </Alert>
      )}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the Notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

export default NotificationProvider;
